import { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import { rupee } from './labPrint';
import { errMsg } from './labApi';

const TYPES = [{ k: 'gmr', label: 'GMR (Sugar Test)' }, { k: 'ecg', label: 'ECG' }];
const EMPTY = { count: '', cash: '', upi: '' };
const num = (v) => (v === '' ? 0 : parseFloat(v) || 0);
const clean = (v) => v.replace(/[^0-9.]/g, '');

const ServiceCard = ({ label, auto, mark, setMark, k }) => {
  const diff = num(mark.count) - auto.count;
  const total = num(mark.cash) + num(mark.upi);
  return (
    <div className="border rounded-xl p-4 bg-white space-y-3" data-testid={`lab-nursing-${k}`}>
      <div className="flex justify-between items-start">
        <div>
          <p className="font-bold">{label}</p>
          <p className="text-xs text-slate-500">Nurse entries: <b data-testid={`lab-nursing-${k}-auto-count`}>{auto.count}</b> • {rupee(auto.amount)}</p>
        </div>
        {mark.count !== '' && diff !== 0 && (
          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700" data-testid={`lab-nursing-${k}-diff`}>{diff > 0 ? '+' : ''}{diff} vs nurse</span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[['count', 'Total taken', 'numeric'], ['cash', 'Cash ₹', 'decimal'], ['upi', 'UPI ₹', 'decimal']].map(([f, l, m]) => (
          <div key={f} className="space-y-0.5">
            <span className="text-[11px] text-slate-500">{l}</span>
            <Input inputMode={m} value={mark[f]} onChange={(e) => setMark({ ...mark, [f]: f === 'count' ? e.target.value.replace(/\D/g, '') : clean(e.target.value) })} className="h-9" data-testid={`lab-nursing-${k}-${f}`} />
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-600">Collected: <b>{rupee(total)}</b>{auto.count > 0 && <> • expected {rupee(auto.amount)}</>}</p>
      {auto.patients.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-teal-700 font-semibold">Patients ({auto.patients.length})</summary>
          <ul className="mt-1 space-y-0.5">
            {auto.patients.map((p, i) => <li key={i} className="flex justify-between"><span>{p.token && `${p.token} • `}{p.name}{p.qty > 1 && ` ×${p.qty}`}</span><span>{rupee(p.amount)}</span></li>)}
          </ul>
        </details>
      )}
    </div>
  );
};

export const LabNursingPanel = ({ api, today, onSaved }) => {
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [marks, setMarks] = useState({ gmr: EMPTY, ecg: EMPTY });

  const load = useCallback(() => api.get('/nursing', { date }).then((d) => {
    setData(d);
    const m = d.marked?.items || {};
    setMarks(Object.fromEntries(TYPES.map(({ k }) => [k, m[k] ? { count: String(m[k].count), cash: String(m[k].cash || ''), upi: String(m[k].upi || '') } : EMPTY])));
  }).catch(() => setData(null)), [api, date]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.put(`/nursing?date=${date}`, { items: Object.fromEntries(TYPES.map(({ k }) => [k, { count: num(marks[k].count), cash: num(marks[k].cash), upi: num(marks[k].upi) }])) });
      toast.success('Nursing ECG / GMR tally saved');
      load(); onSaved();
    } catch (e) { toast.error(errMsg(e, 'Could not save')); }
  };

  return (
    <Card className="p-5 space-y-4" data-testid="lab-nursing-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-bold">From Nursing — ECG &amp; GMR</h3>
        <Input type="date" value={date} max={today} onChange={(e) => e.target.value && setDate(e.target.value)} className="h-8 w-40" data-testid="lab-nursing-date" />
      </div>
      {data && (
        <>
          <div className="grid md:grid-cols-2 gap-3">
            {TYPES.map(({ k, label }) => (
              <ServiceCard key={k} k={k} label={label} auto={data.auto[k]} mark={marks[k]} setMark={(v) => setMarks({ ...marks, [k]: v })} />
            ))}
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500" data-testid="lab-nursing-saved-info">{data.marked ? `Last saved by ${data.marked.by} • ${new Date(data.marked.at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : 'Not marked yet'}</p>
            <Button onClick={save} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="lab-nursing-save"><Save className="h-4 w-4 mr-1" /> Save</Button>
          </div>
        </>
      )}
    </Card>
  );
};
